import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { forkJoin } from 'rxjs';

import * as moment from 'moment';

import { ToastrService } from 'ngx-toastr';
import { EventsService } from '../../../services/events.service';
import { FixturesService } from '../../../services/fixtures.service';
import { RacesService } from '../../../services/races.service';
import { EntriesService } from '../../../services/entries.service';

@Component({
  selector: 'app-fixture-racecards',
  templateUrl: './fixture-racecards.component.html',
  styleUrls: ['./fixture-racecards.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FixtureRacecardsComponent implements OnInit, OnDestroy {

  withRPC: boolean;
  meta: {
    started: boolean,
    loading: boolean,
    status: string,
  };
  fixture: any;
  races: any[];
  prepareReady = false;
  layoutReady = false;
  prepareLog: string[] = [];
  prepareProgress = 0;
  elapsedLabel = '0s';
  private readonly prepareLogTailSize = 10;
  private prepareStartedAt: number = null;
  private elapsedTimer: any = null;

  /** Last N log lines (tail-style) so the current step stays visible. */
  get prepareLogTail(): string[] {
    const size = this.prepareLogTailSize;
    return this.prepareLog.length <= size
      ? this.prepareLog
      : this.prepareLog.slice(this.prepareLog.length - size);
  }


  constructor(
    private notification: ToastrService,
    private route: ActivatedRoute,
    private eventsService: EventsService,
    private fixturesService: FixturesService,
    private racesService: RacesService,
    private entriesService: EntriesService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.withRPC = false;
    this.meta = {
      started: false,
      loading: false,
      status: 'initializing',
    };
    this.races = [];

    this.fixture = {
      fixtureId: Number(this.route.snapshot.paramMap.get('fixtureId')),
      fixtureYear: this.route.snapshot.paramMap.get('fixtureYear')
    };

    // Load course/date for the page title before Prepare is clicked
    this.loadFixtureHeader();
  }

  get fixtureCourseLabel(): string {
    return (this.fixture && this.fixture.courseName) || '';
  }

  get fixtureDateLabel(): string {
    if (!this.fixture || !this.fixture.fixtureDate) {
      return '';
    }
    const parsed = moment(this.fixture.fixtureDate);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
  }

  private loadFixtureHeader(): void {
    const fixtureYear = this.fixture.fixtureYear;
    const fixtureId = this.fixture.fixtureId;
    this.fixturesService.getFixtureInfo({ fixtureYear, fixtureId }).subscribe(
      res => {
        this.fixture = Object.assign({}, this.fixture, res, {
          fixtureYear: res.year || fixtureYear,
          fixtureId: res.fixtureId || fixtureId
        });
        this.eventsService.changeFixtureData(this.fixture);
        this.changeDetector.detectChanges();
      },
      () => {
        // Title stays generic if header load fails; Prepare can still retry
      }
    );
  }

  ngOnDestroy() {
    this.stopElapsedTimer();
  }

  start() {
    this.route.params.subscribe(params => {
      if (Object.keys(params).length) {
        this.prepareReady = false;
        this.layoutReady = false;
        this.races = [];
        this.prepareLog = [];
        this.prepareProgress = 0;
        this.meta.started = true;
        this.meta.loading = true;
        this.startElapsedTimer();
        this.setPrepareStatus(
          this.withRPC
            ? 'Starting prepare (with RPC comments/enquiries)…'
            : 'Starting prepare (without RPC)…',
          2
        );
        this.getFixtureInfo(this.fixture.fixtureYear, this.fixture.fixtureId);
      }
    });
  }

  getFixtureInfo = (fixtureYear, fixtureId) => {
    this.setPrepareStatus('Loading fixture information…', 8);
    this.fixturesService.getFixtureInfo({ fixtureYear, fixtureId }).subscribe(
      res => {
        this.fixture = res;
        this.fixture.fixtureYear = this.fixture.year;
        this.eventsService.changeFixtureData(this.fixture);
        this.setPrepareStatus(
          `Fixture loaded: ${this.fixture.courseName || 'meeting'} (${moment(this.fixture.fixtureDate).format('DD/MM/YYYY')})`,
          15
        );
        this.getFixtureRaces(this.fixture.fixtureYear, this.fixture.fixtureId);
      },
      err => {
        this.notification.error(`Could't get the Fixture information: ${this.eventsService.getApiErrorMessage(err)}`, 'Error!');
        this.failPrepare();
      }
    );
  }

  getFixtureRaces = (fixtureYear, fixtureId): void => {
    this.setPrepareStatus('Loading race list…', 20);
    this.fixturesService.getFixtureRaces({ fixtureYear, fixtureId }).subscribe(
      res => {
        this.races = res;
        this.setPrepareStatus(
          `Found ${this.races.length} race${this.races.length === 1 ? '' : 's'} — loading runners and race details…`,
          25
        );
        forkJoin(this.getEntries(this.races), this.getRaceInfo(this.races)).subscribe(
          () => console.log(this.fixture, this.races),
          err => {
            console.log(err);
            this.failPrepare();
          },
          () => {
            this.setPrepareStatus('Finalising layout…', 95);
            setTimeout(() => {
              this.meta.started = false;
              this.meta.loading = false;
              this.prepareReady = true;
              this.stopElapsedTimer();
              this.setPrepareStatus(`Ready to print (${this.elapsedLabel}).`, 100);
              setTimeout(() => {
                // RPC only: measure/pack/stretch. Non-RPC uses customHeightPx formula.
                if (this.withRPC) {
                  this.stretchEntryHeights();
                }
                this.layoutReady = true;
                this.changeDetector.detectChanges();
              }, 300);
            }, 1000);
          }
        );
      },
      err => {
        this.notification.error(`Could't get the Fixture Entries with Sampling Requests: ` +
        `${this.eventsService.getApiErrorMessage(err)}`, 'Error!');
        this.failPrepare();
      }
    );
  }

  getRaceInfo = (races) => {
    return new Promise((resolve, reject) => {
      this.setPrepareStatus('Loading race information…', 30);
      const raceInfoRequests = races.map(race => this.racesService.getRaceInfo(race));
      forkJoin(...raceInfoRequests).subscribe(
        results => {
          results.forEach((raceInfo, index) => {
            if (!raceInfo || !raceInfo['fixtureId']) {
              console.log('Failed to load data:', races[index].raceId, races[index]);
              this.notification.error(`Could't get race info for the ${moment(races[index].raceDateTime).format('HH:mm')} race.`, 'Error!');
              this.races[index].info = {};
            } else {
              this.races[index].info = raceInfo;
            }
          });
          this.setPrepareStatus('Race information loaded.', this.withRPC ? 35 : 40);
          resolve();
        },
        errors => {
          console.log('Failed to load data:', errors);
          this.notification.error(`Could't get info for races.`, 'Error!');
          this.failPrepare();
          reject(errors);
        }
      );
    });
  }

  getEntries = (races) => {
    return new Promise( (resolve, reject) => {
      this.setPrepareStatus('Loading runners…', 32);
      let racesCompleted = 0;
      const entriesRequests = races.map(race => this.racesService.getRaceEntries(race));
      forkJoin(...entriesRequests).subscribe(
        results => {
          results.forEach((entries: [any], indexRace) => {
            if (!entries || !entries[0]) {
              console.log('Failed to load data:', races[indexRace].raceId, races[indexRace]);
              this.notification.error(
                `Could't get some details for the ${moment(races[indexRace].raceDateTime).format('HH:mm')} race.`,
                'Error!'
              );
              this.races[indexRace].entries = [];
              if (this.withRPC) {
                racesCompleted++;
                this.setPrepareProgress(this.rpcProgress(racesCompleted, races.length));
                if (racesCompleted === races.length) {
                  resolve();
                }
              } else {
                racesCompleted++;
                if (racesCompleted === races.length) {
                  this.setPrepareStatus('Runners loaded.', 85);
                  resolve();
                }
              }
            } else {
              for (let i = 0; i < entries.length; i++) {
                entries[i] = this.processWearingCodes(entries[i]);
                entries[i].shortDispensation = entries[i].dispensation
                  ? entries[i].shoesDispensation.dispensation.split(' ').splice(0, 4).join(' ')
                  : null;
                entries[i].minHeightPx = 58;
                entries[i].imageSize = 50;
                entries[i].customFontSize = 1;
                entries[i].stretchMinHeightPx = null;
                entries[i].forcePageBreak = false;
                if (this.withRPC) {
                  // Natural height; stretchEntryHeights() fills leftover page space
                  entries[i].customHeightPx = null;
                } else if (entries.length < 20) {
                  // Proven page-fill formula (pre measure/stretch regression):
                  // scale row height as if the page held 20 runners.
                  entries[i].customHeightPx = (20 / entries.length) * 58 + 5;
                  entries[i].imageSize = (20 / entries.length) * entries[i].imageSize;
                  entries[i].customFontSize = (20 / entries.length) * entries[i].customFontSize + 5;
                  if (entries[i].customHeightPx > 300) {
                    entries[i].customHeightPx = 300;
                  }
                  // Bigger silks for small/medium fields; large fields (13+)
                  // stay at 52. Clamp to row so padding (8px top+bottom)
                  // never clips into the horizontal rules.
                  const maxSilk = entries.length <= 8 ? 90 : entries.length <= 12 ? 66 : 52;
                  if (entries[i].imageSize > maxSilk) {
                    entries[i].imageSize = maxSilk;
                  }
                  if (entries.length <= 12) {
                    const maxFit = entries[i].customHeightPx - 16;
                    if (entries[i].imageSize > maxFit) {
                      entries[i].imageSize = maxFit;
                    }
                  }
                  if (entries[i].customFontSize > 1.15) {
                    entries[i].customFontSize = 1.15;
                  }
                } else {
                  entries[i].customHeightPx = 58;
                }
              }
              this.races[indexRace].entries = entries;
              if (this.withRPC) {
                const raceTime = moment(this.races[indexRace].raceDateTime).format('HH:mm');
                this.setPrepareStatus(
                  `Loading comments/enquiries for ${raceTime} (${entries.length} runners)…`,
                  this.rpcProgress(racesCompleted, races.length)
                );
                const entryLatestRequests = entries.map(
                  entry => this.getLatestEntryNotes(entry.horseId, this.races[indexRace].raceDateTime)
                );
                forkJoin(...entryLatestRequests).subscribe(
                  res => {
                    for (let indexEntry = 0; indexEntry < this.races[indexRace].entries.length; indexEntry++) {
                      this.races[indexRace].entries[indexEntry].latest = res[indexEntry] || {};
                    }
                  },
                  err => reject(err),
                  () => {
                    racesCompleted++;
                    this.setPrepareStatus(
                      `Comments/enquiries loaded for ${racesCompleted}/${races.length} races`,
                      this.rpcProgress(racesCompleted, races.length)
                    );
                    if (racesCompleted === races.length) {
                      resolve();
                    }
                  }
                );
              } else {
                racesCompleted++;
                if (racesCompleted === races.length) {
                  this.setPrepareStatus('Runners loaded.', 85);
                  resolve();
                }
              }
            }
          });
        },
        errors => {
          console.log('Failed to load data:', errors);
          this.notification.error(`Could't get some details for racecards.`, 'Error!');
          this.failPrepare();
          reject(errors);
        }
      );
    });
  }

  private rpcProgress(completed: number, total: number): number {
    if (!total) {
      return 90;
    }
    // RPC work sits in the bulk of the bar (40–90%)
    return Math.min(90, Math.round(40 + (completed / total) * 50));
  }

  private setPrepareProgress(progress: number): void {
    if (typeof progress === 'number' && progress >= this.prepareProgress) {
      this.prepareProgress = Math.min(100, progress);
    }
  }

  private setPrepareStatus(message: string, progress?: number): void {
    this.meta.status = message;
    if (typeof progress === 'number') {
      this.setPrepareProgress(progress);
    }
    const stamp = moment().format('HH:mm:ss');
    this.prepareLog = this.prepareLog.concat(`[${stamp}] ${message}`);
    this.changeDetector.detectChanges();
  }

  private startElapsedTimer(): void {
    this.stopElapsedTimer();
    this.prepareStartedAt = Date.now();
    this.elapsedLabel = '0s';
    this.elapsedTimer = setInterval(() => {
      const seconds = Math.floor((Date.now() - this.prepareStartedAt) / 1000);
      if (seconds < 60) {
        this.elapsedLabel = `${seconds}s`;
      } else {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        this.elapsedLabel = `${mins}m ${secs}s`;
      }
      this.changeDetector.detectChanges();
    }, 1000);
  }

  private stopElapsedTimer(): void {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  private failPrepare(): void {
    this.meta.started = false;
    this.meta.loading = false;
    this.prepareReady = false;
    this.stopElapsedTimer();
    this.setPrepareStatus('Prepare failed — you can try again.');
  }

  processWearingCodes(entry): void {
    if (entry.headGearCodeDefinition) {
      // Assuming the two arrays' elements are in the same order, which should be unless the backend code changes
      const codesArray = entry.headGearCodeDefinition.abbreviation.split(' ');
      for (let i = 0; i < entry.headGearCodeDefinition.firstTimeIndicator.length; i++) {
        entry.headGearCodeDefinition.firstTimeIndicator[i].code = codesArray[i];
      }
      entry.headgear = entry.headGearCodeDefinition.firstTimeIndicator;
    }
    return entry;
  }

  getLatestEntryNotes = (horseId, raceDate) => {
    return new Promise((resolve, reject) => {
      forkJoin(
        this.entriesService.getEntryOldComments(horseId, true, moment(raceDate).format('YYYYMMDD')),
        this.entriesService.getEntryOldEnquiries(horseId, moment(raceDate).format('YYYYMMDD'))
      ).subscribe(
        res => resolve({
          comment: {
            date: res[0]['data'][0] ? res[0]['data'][0]['dateTime'] : null,
            courseShortName: res[0]['data'][0] ? res[0]['data'][0]['courseNameShort'] : null,
            rpc: res[0]['data'][0] ? res[0]['data'][0]['rpcComments'] : null
          },
          enquiry: {
            date: res[1]['data'][0] ? res[1]['data'][0]['updatedAt'] : null,
            courseShortName: res[1]['data'][0] && res[1]['data'][0]['race']
              ? res[1]['data'][0]['race']['courseShortName']
              : null,
            offence: res[1]['data'][0] ? res[1]['data'][0]['offence'] : null
          }
        }),
        err => reject(err)
      );
    });
  }

  doPrint = () => {
    if (this.withRPC) {
      this.stretchEntryHeights();
      this.changeDetector.detectChanges();
    }
    setTimeout(() => window.print(), 50);
  }

  /**
   * With-RPC only: measure natural heights with compact silks, pack pages
   * (prefer keeping ~10–12 runner fields on one page), then stretch rows to
   * the non-RPC formula page budget and scale silks per-race only:
   *   targetSum = count * ((20 / count) * 58 + 5)
   *   target_i  = natural_i * (targetSum / naturalSum)
   */
  stretchEntryHeights(): void {
    if (!this.withRPC || !this.races || !this.races.length) {
      return;
    }

    // Compact silks for measure/pack — large silks before measure were forcing
    // 10-runner races onto two pages.
    this.races.forEach(race => {
      (race.entries || []).forEach(entry => {
        entry.stretchMinHeightPx = null;
        entry.forcePageBreak = false;
        entry.imageSize = 50;
      });
    });
    this.changeDetector.detectChanges();

    const formulaPageBudget = (count: number) => count * ((20 / count) * 58 + 5);
    // Slightly above the plain 20×58 budget so moderate RPC notes on ~10
    // runners still pack onto one page; stretch then fills leftover.
    const packBudget = 20 * 72;
    // Clear gap from row borders; allow larger silks when the field is small
    const silkPad = 8;

    this.races.forEach((race, raceIndex) => {
      if (!race.entries || !race.entries.length) {
        return;
      }

      const raceEl = document.querySelector(
        `.print-visible .race-sheet[data-race-index="${raceIndex}"]`
      ) as HTMLElement;
      if (!raceEl) {
        return;
      }

      const entryEls = Array.from(raceEl.querySelectorAll('.entry-block')) as HTMLElement[];
      const naturalHeights = entryEls.map(el => Math.max(1, el.offsetHeight));
      const pages = this.packEntriesIntoPages(naturalHeights, packBudget, packBudget);

      pages.forEach((page, pageIndex) => {
        const count = page.indices.length;
        if (!count) {
          return;
        }

        page.indices.forEach((entryIndex, i) => {
          race.entries[entryIndex].forcePageBreak = pageIndex > 0 && i === 0;
        });

        const naturalSum = page.indices.reduce((sum, idx) => sum + naturalHeights[idx], 0);
        const targetSum = formulaPageBudget(count);

        if (naturalSum >= targetSum) {
          page.indices.forEach(entryIndex => {
            race.entries[entryIndex].stretchMinHeightPx = naturalHeights[entryIndex];
          });
          return;
        }

        const scale = targetSum / naturalSum;
        let used = 0;
        page.indices.forEach((entryIndex, i) => {
          let h: number;
          if (i === count - 1) {
            h = Math.max(naturalHeights[entryIndex], targetSum - used);
          } else {
            h = Math.max(naturalHeights[entryIndex], Math.round(naturalHeights[entryIndex] * scale));
            used += h;
          }
          race.entries[entryIndex].stretchMinHeightPx = h;
        });
      });

      // One silk size for this race only — bigger for small fields, always
      // leave silkPad clear of the horizontal rules.
      const n = race.entries.length;
      const maxSilk = n <= 8 ? 72 : n <= 12 ? 60 : 52;
      const formulaSilk = Math.min(maxSilk, (20 / n) * 50);
      const minRow = Math.min.apply(
        null,
        race.entries.map(e => e.stretchMinHeightPx || 58)
      );
      const silkFromRow = Math.max(40, Math.min(maxSilk, minRow - silkPad * 2));
      const silkSize = Math.min(maxSilk, Math.max(formulaSilk, silkFromRow));
      race.entries.forEach(entry => {
        entry.imageSize = silkSize;
      });
    });

    this.changeDetector.detectChanges();
  }

  /**
   * Contiguous pack into the fewest pages, balanced across pages
   * (avoids greedy 12+3 when ~8+7 would fill both sheets better).
   */
  private packEntriesIntoPages(
    heights: number[],
    page1Usable: number,
    pageRestUsable: number
  ): { indices: number[]; height: number }[] {
    const n = heights.length;
    if (!n) {
      return [];
    }

    const prefix = [0];
    heights.forEach((h, i) => prefix.push(prefix[i] + h));
    const rangeSum = (from: number, to: number) => prefix[to] - prefix[from];

    const pageUsableAt = (pageIndex: number) => pageIndex === 0 ? page1Usable : pageRestUsable;

    const minPages = this.minPagesNeeded(heights, page1Usable, pageRestUsable);
    if (minPages <= 1) {
      return [{ indices: heights.map((_, i) => i), height: rangeSum(0, n) }];
    }

    // DP: place first `i` entries into exactly `p` pages; minimize imbalance score
    // prev[i][p] = start index of the last page when placing i entries in p pages
    const INF = Number.POSITIVE_INFINITY;
    const bestMax = Array.from({ length: n + 1 }, () => Array(minPages + 1).fill(INF));
    const prevSplit = Array.from({ length: n + 1 }, () => Array(minPages + 1).fill(-1));
    bestMax[0][0] = 0;

    for (let p = 1; p <= minPages; p++) {
      for (let i = p; i <= n; i++) {
        for (let j = p - 1; j < i; j++) {
          const pageHeight = rangeSum(j, i);
          const usable = pageUsableAt(p - 1);
          if (pageHeight > usable || bestMax[j][p - 1] === INF) {
            continue;
          }
          // Minimize the worst page fill ratio so leftover stretch is even
          const fill = pageHeight / usable;
          const score = Math.max(bestMax[j][p - 1], fill);
          if (score < bestMax[i][p]) {
            bestMax[i][p] = score;
            prevSplit[i][p] = j;
          }
        }
      }
    }

    // Reconstruct page boundaries
    if (bestMax[n][minPages] === INF) {
      // Fallback — should be rare
      return this.greedyPack(heights, page1Usable, pageRestUsable);
    }

    const bounds = [n];
    let i = n;
    for (let p = minPages; p >= 1; p--) {
      const j = prevSplit[i][p];
      bounds.push(j);
      i = j;
    }
    bounds.reverse();

    const pages: { indices: number[]; height: number }[] = [];
    for (let p = 0; p < bounds.length - 1; p++) {
      const from = bounds[p];
      const to = bounds[p + 1];
      const indices = [];
      for (let idx = from; idx < to; idx++) {
        indices.push(idx);
      }
      pages.push({ indices, height: rangeSum(from, to) });
    }
    return pages;
  }

  private minPagesNeeded(
    heights: number[],
    page1Usable: number,
    pageRestUsable: number
  ): number {
    return this.greedyPack(heights, page1Usable, pageRestUsable).length || 1;
  }

  private greedyPack(
    heights: number[],
    page1Usable: number,
    pageRestUsable: number
  ): { indices: number[]; height: number }[] {
    const pages: { indices: number[]; height: number }[] = [];
    let current = { indices: [] as number[], height: 0 };
    let usable = page1Usable;

    heights.forEach((height, index) => {
      if (current.indices.length && current.height + height > usable) {
        pages.push(current);
        current = { indices: [], height: 0 };
        usable = pageRestUsable;
      }
      current.indices.push(index);
      current.height += height;
    });
    if (current.indices.length) {
      pages.push(current);
    }
    return pages;
  }

  private getPrintPageUsableHeightPx(): number {
    // A4 (297mm) minus 0.5cm top+bottom (=10mm) → 287mm printable.
    // Use both a CSS-mm probe and a 96dpi fallback; take the larger so we
    // don't under-fill when the probe resolves mm too small.
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;left:-99999px;top:0;visibility:hidden;height:287mm;width:10px;pointer-events:none;';
    document.body.appendChild(probe);
    const probed = probe.offsetHeight;
    document.body.removeChild(probe);
    const fallback = Math.floor(287 * 96 / 25.4); // ~1085px
    return Math.max(probed || 0, fallback);
  }
}
